import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Workflow, DollarSign, Users, Headphones, FileCheck, ArrowRight } from 'lucide-react';

const AutomationRPA = () => {
  const services = [
    {
      icon: DollarSign,
      title: 'Finance Automation',
      description: 'Invoices, reconciliation, GST, payments, reporting automated with AI. Reduce manual finance operations by 80%.',
      features: ['Invoice Processing', 'Payment Reconciliation', 'GST Filing', 'Financial Reporting'],
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop'
    },
    {
      icon: Workflow,
      title: 'IT Operations Automation',
      description: 'Ticket handling, alerts, patching, monitoring automated. Let AI manage your IT infrastructure 24/7.',
      features: ['Ticket Management', 'System Monitoring', 'Auto-Patching', 'Alert Response'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      icon: Users,
      title: 'HR Automation',
      description: 'Onboarding, payroll, compliance, attendance automated. Free HR teams for strategic work.',
      features: ['Employee Onboarding', 'Payroll Processing', 'Compliance Management', 'Attendance Tracking'],
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=400&fit=crop'
    },
    {
      icon: Headphones,
      title: 'Sales & CRM Automation',
      description: 'Lead management, follow-ups, renewals, invoicing automated. Never miss a sales opportunity.',
      features: ['Lead Management', 'Automated Follow-ups', 'Renewal Tracking', 'Invoice Generation'],
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&h=400&fit=crop'
    },
    {
      icon: FileCheck,
      title: 'Compliance & Audit Automation',
      description: 'Regulatory reporting, data extraction and validation automated. Stay compliant effortlessly.',
      features: ['Regulatory Reporting', 'Data Validation', 'Audit Trail', 'Compliance Monitoring'],
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=400&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-900 via-red-900 to-pink-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1920&h=800&fit=crop" 
            alt="Automation" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Workflow className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-orange-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Business Process Automation & RPA</h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-4 font-semibold">
              AI-Driven Automation for Enterprise Operations
            </p>
            <p className="text-lg md:text-xl text-orange-200 mb-8">
              AI + RPA + workflows replace manual teams with digital workers that operate continuously. Automate repetitive tasks and free your team for strategic work.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-orange-900 hover:bg-orange-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Automate Your Operations
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Automation Solutions</h2>
            <div className="w-24 h-1 bg-orange-600 mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Digital workers that handle tasks 24/7 with 100% accuracy
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-orange-600 rounded-full"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">Why Automate?</h2>
            <div className="w-24 h-1 bg-orange-600 mx-auto mb-12"></div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">80% Cost Reduction</h3>
                <p className="text-gray-600">Replace manual processes with digital workers. Pay once, run forever.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">100% Accuracy</h3>
                <p className="text-gray-600">Eliminate human errors in repetitive tasks. Perfect execution every time.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">24/7 Operations</h3>
                <p className="text-gray-600">Digital workers never sleep. Continuous processing without breaks.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance Ready</h3>
                <p className="text-gray-600">Audit trails, governance and regulatory compliance built-in.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-orange-900 to-pink-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Start Your Automation Journey</h2>
          <p className="text-lg md:text-xl mb-8 text-orange-100 max-w-2xl mx-auto">
            Let digital workers handle the routine. Free your team for innovation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-900 hover:bg-orange-50 text-lg px-8 transition-all hover:scale-105">
              Book Automation Assessment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AutomationRPA;